import { createContext, useContext, ReactNode, useState, useEffect, useRef, useCallback } from 'react'
import { BrandKit, AIJobStatus, Archetype } from 'types'
import { StoreSnapshot } from '@tldraw/tldraw'
import { useLastUsedLanguage } from 'hooks/queries/useLastUsedLanguage'
import { useGenerateDesignContentSync } from 'hooks/queries/workspaces/useGenerateDesignContentSync'
import { reportError } from 'utils/error'
import { useSharedBrandKit } from './SharedBrandKitProvider'

interface ContentPreviewState {
  brandKit?: BrandKit
  generatedContent: StoreSnapshot<any> | null
  isGenerating: boolean
}

export const ContentPreviewCtx = createContext<ContentPreviewState | null>(null)

export const useContentPreviewState = () => {
  const ctx = useContext(ContentPreviewCtx)
  if (!ctx) throw new Error('useContentPreviewState must be used within ContentPreviewProvider')
  return ctx
}

export const ContentPreviewProvider = ({
  children,
  archetype = Archetype.INSTAGRAM_POST_PHOTO_TEXT,
  width = 1080,
  height = 1080,
  index = 0,
}: {
  children: ReactNode
  archetype?: Archetype
  width?: number
  height?: number
  index?: number
}) => {
  const { brandKit, isLoading: isBrandKitLoading } = useSharedBrandKit()
  const [generatedContent, setGeneratedContent] = useState<StoreSnapshot<any> | null>(null)
  const [isGenerating, setIsGenerating] = useState<boolean>(false)
  const controllerRef = useRef<AbortController | null>(null)
  const hasStartedGeneration = useRef<boolean>(false)

  const { data: defaultLanguageQuery } = useLastUsedLanguage()
  const defaultLanguage = defaultLanguageQuery?.language
  const generateMutation = useGenerateDesignContentSync({ retry: 3 })

  const startGeneration = useCallback(
    ({ kit = brandKit, archetype: customArchetype }: { kit?: BrandKit; archetype?: Archetype }) => {
      if (!kit || isGenerating || isBrandKitLoading || hasStartedGeneration.current) {
        return
      }
      hasStartedGeneration.current = true
      setIsGenerating(true)

      if (controllerRef.current) {
        controllerRef.current.abort()
      }

      const controller = new AbortController()
      controllerRef.current = controller

      const selectedArchetype = customArchetype || archetype

      const selectedTopic =
        kit.brand_voice?.voice_params?.purpose?.[0] ||
        kit.brand_voice?.voice_params?.tone?.[0] ||
        `Content for ${kit.name}`

      generateMutation.mutate(
        {
          archetype: selectedArchetype,
          topic: selectedTopic,
          language: defaultLanguage ?? undefined,
          brand_voice_id: kit.brand_voice?.id,
          brand_kit_id: kit.id,
          brand_visual_kit_id: kit.brand_visual_kit?.id,
          apply_brand: true,
          width,
          height,
          signal: controller.signal,
          media_priority: kit.content_preferences?.media_priority,
          featured_media: kit.content_preferences?.featured_media,
        },
        {
          onSuccess: (data) => {
            try {
              if (!data?.generated_content) {
                throw new Error('No generated content received from API')
              }

              setGeneratedContent(JSON.parse(data.generated_content))
            } catch (err) {
              reportError(err)
              hasStartedGeneration.current = false
            } finally {
              setIsGenerating(false)
            }
          },
          onError: (error) => {
            reportError(error)
            setIsGenerating(false)
            hasStartedGeneration.current = false
          },
        },
      )
    },
    [brandKit, isGenerating, isBrandKitLoading, generateMutation, defaultLanguage, archetype, width, height],
  )

  useEffect(() => () => controllerRef.current?.abort(), [])

  useEffect(() => {
    if (
      brandKit &&
      brandKit.status === AIJobStatus.Successful &&
      !generatedContent &&
      !isGenerating &&
      !isBrandKitLoading &&
      !hasStartedGeneration.current
    ) {
      startGeneration({ kit: brandKit, archetype })
    }
  }, [brandKit, generatedContent, isGenerating, isBrandKitLoading, archetype, startGeneration])

  const value = {
    brandKit,
    generatedContent,
    isGenerating,
  }

  return <ContentPreviewCtx.Provider value={value}>{children}</ContentPreviewCtx.Provider>
}
